import { getConfig } from './config';
import { getIdToken } from './auth';

export interface ReportSummary {
  reportId: string;
  title?: string;
  publishedAt?: string;
  untagged?: boolean;
}

export interface ReportsPage {
  reports: ReportSummary[];
  count: number;
  nextCursor?: string;
  scope?: string | null;
  availableScopes?: string[];
}

export interface ReportAttachment {
  attachmentId?: string;
  filename?: string;
  contentType?: string;
  url?: string | null;
  downloadUrl?: string | null;
}

export interface ReportDetail {
  reportId: string;
  title?: string;
  body?: string;
  source?: string;
  tags?: string[];
  publishedAt?: string;
  // The relevant/flagged content (e.g. a channel hosting pirated media);
  // present only on some reports. Stored defanged per
  // draft-grimminck-safe-ioc-sharing (hxxps://example[.]com), so it is
  // display-only and never rendered as a link.
  contentUrl?: string;
  // Additional related links supplied by the source (e.g. ZeroFox
  // supplemental_urls); present only on some reports. Defanged like contentUrl.
  supplementalUrls?: string[];
  attachments?: ReportAttachment[];
  // Present only for admins (the API omits scopes from ordinary reads); used to
  // prefill the scope-fix editor.
  scopes?: string[];
}

/** Raised when the API rejects the token; the caller should re-authenticate. */
export class UnauthorizedError extends Error {}

/**
 * Raised on a 403: the caller is authenticated but not allowed. Re-auth can't
 * fix authorization, so callers should surface this rather than loop on login.
 */
export class ForbiddenError extends Error {}

/** Raised when a report or scope isn't visible to the caller (404 by design). */
export class NotFoundError extends Error {}

interface RequestOptions {
  params?: Record<string, string | undefined>;
  signal?: AbortSignal;
  notFoundMessage?: string;
  method?: string;
  body?: unknown;
}

async function fetchJson<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { params, signal, notFoundMessage = 'Not found.', method = 'GET', body } = options;
  const token = await getIdToken();
  if (!token) throw new UnauthorizedError('No valid session.');

  const url = new URL(path, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value) url.searchParams.set(key, value);
    }
  }

  const headers: Record<string, string> = { Authorization: token };
  const init: RequestInit = { method, headers, signal };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  const resp = await fetch(url.toString(), init);
  // 401 means the token is bad (re-auth); 403 means not allowed (don't loop on
  // login); 404 means the requested resource isn't visible to the caller.
  if (resp.status === 401) throw new UnauthorizedError('Request rejected (401).');
  if (resp.status === 403) throw new ForbiddenError('You are not authorized for this scope.');
  if (resp.status === 404) throw new NotFoundError(notFoundMessage);
  if (!resp.ok) throw new Error(`Request failed (${resp.status}).`);

  return resp.json() as Promise<T>;
}

export interface ReportSearch {
  query?: string;
  searchContent?: boolean;
  from?: string;
  to?: string;
}

export function listReports(
  scope?: string,
  cursor?: string,
  signal?: AbortSignal,
  search?: ReportSearch,
): Promise<ReportsPage> {
  return fetchJson<ReportsPage>(getConfig().apiBasePath, {
    params: {
      scope,
      cursor,
      query: search?.query,
      // Only send the flag when set; the backend treats a bare flag as no search.
      searchContent: search?.searchContent ? 'true' : undefined,
      from: search?.from,
      to: search?.to,
    },
    signal,
    notFoundMessage: 'No reports found for that scope.',
  });
}

export function getReport(reportId: string, signal?: AbortSignal): Promise<ReportDetail> {
  return fetchJson<ReportDetail>(`${getConfig().apiBasePath}/${encodeURIComponent(reportId)}`, {
    signal,
    notFoundMessage: 'Report not found.',
  });
}

export interface CapabilitiesResponse {
  canCreate: boolean;
  canManageScopes: boolean;
  availableScopes: string[];
}

export interface UpdateReportScopesResult {
  reportId: string;
  scopes: string[];
}

export function updateReportScopes(
  reportId: string,
  scopes: string[],
  signal?: AbortSignal,
): Promise<UpdateReportScopesResult> {
  return fetchJson<UpdateReportScopesResult>(`${getConfig().apiBasePath}/${encodeURIComponent(reportId)}`, {
    method: 'PATCH',
    body: { scopes },
    signal,
    notFoundMessage: 'Report not found.',
  });
}

export interface DeleteReportResult {
  reportId: string;
  deleted: boolean;
}

export function deleteReport(reportId: string, signal?: AbortSignal): Promise<DeleteReportResult> {
  return fetchJson<DeleteReportResult>(`${getConfig().apiBasePath}/${encodeURIComponent(reportId)}`, {
    method: 'DELETE',
    signal,
    notFoundMessage: 'Report not found.',
  });
}

export function fetchCapabilities(signal?: AbortSignal): Promise<CapabilitiesResponse> {
  return fetchJson<CapabilitiesResponse>(getConfig().capabilitiesBasePath, {
    signal,
    notFoundMessage: 'Could not load your permissions.',
  });
}

export interface AttachmentInput {
  filename: string;
  contentType: string;
}

export interface AttachmentUpload {
  attachmentId: string;
  filename: string;
  url: string;
}

export interface CreateReportInput {
  title: string;
  scopes: string[];
  body?: string;
  attachments?: AttachmentInput[];
}

export interface CreateReportResult {
  reportId: string;
  scopes: string[];
  publishedAt: string;
  attachmentUploads?: AttachmentUpload[];
}

export function createReport(input: CreateReportInput, signal?: AbortSignal): Promise<CreateReportResult> {
  return fetchJson<CreateReportResult>(getConfig().apiBasePath, {
    method: 'POST',
    body: input,
    signal,
    notFoundMessage: 'Could not create report.',
  });
}

export async function uploadAttachment(url: string, file: File, signal?: AbortSignal): Promise<void> {
  const resp = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
    signal,
  });
  if (!resp.ok) throw new Error(`Upload failed for "${file.name}" (${resp.status}).`);
}

export type DigestCadence = 'daily' | 'weekly' | 'monthly';

export interface DigestPreferences {
  enabled: boolean;
  cadence: DigestCadence;
  email: string;
}

export function getPreferences(signal?: AbortSignal): Promise<DigestPreferences> {
  return fetchJson<DigestPreferences>(getConfig().preferencesBasePath, {
    signal,
    notFoundMessage: 'Preferences not found.',
  });
}

export function savePreferences(
  prefs: { enabled: boolean; cadence: DigestCadence },
  signal?: AbortSignal,
): Promise<DigestPreferences> {
  return fetchJson<DigestPreferences>(getConfig().preferencesBasePath, {
    method: 'PUT',
    body: prefs,
    signal,
  });
}
